import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiClient } from '../services/apiClient'
import { useAuth } from '../contexts/AuthContext'
import { Card, CardContent } from '../components/ui/card'

type UserRole = 'admin' | 'manager' | 'member'

interface PromptTemplate {
  id: string
  scope: 'system' | 'user'
  owner_user_id: string | null
  name: string
  agent_name: string
  industry: string | null
  proposal_type: string | null
  content: string
  created_at: string
  updated_at: string
}

interface SeedPromptsResponse {
  created: number
  updated: number
}

interface AdminUser {
  id: string
  email: string
  full_name: string
  role: UserRole
  is_active: boolean
  created_at: string
  last_login: string | null
  invite_url?: string | null
}

interface AdminUserListResponse {
  users: AdminUser[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

interface EmailTemplateData {
  key: string
  subject: string
  body_text: string
  body_html: string
  updated_at: string
}

export function AdminPage() {
  const { user, effectiveUserRole } = useAuth()

  const [users, setUsers] = useState<AdminUser[]>([])
  const [drafts, setDrafts] = useState<Record<string, Pick<AdminUser, 'role' | 'is_active'>>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savingUserIds, setSavingUserIds] = useState<Set<string>>(new Set())

  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [totalPages, setTotalPages] = useState(0)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all')
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all')

  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState<UserRole>('member')
  const [newIsActive, setNewIsActive] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'prompts' | 'emailTemplates' | 'referenceDocs'>('users')

  const [notice, setNotice] = useState<string | null>(null)

  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [inviteModalEmail, setInviteModalEmail] = useState<string | null>(null)
  const [inviteUrl, setInviteUrl] = useState<string>('')
  const [isGeneratingInvite, setIsGeneratingInvite] = useState(false)

  const [systemPrompts, setSystemPrompts] = useState<PromptTemplate[]>([])
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false)
  const [promptSearch, setPromptSearch] = useState('')
  const [promptAgentFilter, setPromptAgentFilter] = useState('')
  const [promptIndustryFilter, setPromptIndustryFilter] = useState('')
  const [promptProposalTypeFilter, setPromptProposalTypeFilter] = useState('')

  const [isSeeding, setIsSeeding] = useState(false)

  const [editingPromptId, setEditingPromptId] = useState<string | null>(null)
  const [promptName, setPromptName] = useState('')
  const [promptAgentName, setPromptAgentName] = useState('project_intake')
  const [promptIndustry, setPromptIndustry] = useState('')
  const [promptProposalType, setPromptProposalType] = useState('')
  const [promptContent, setPromptContent] = useState('')

  const isAdmin = effectiveUserRole === 'admin'
  const isAdminOrManager = effectiveUserRole === 'admin' || effectiveUserRole === 'manager'

  const [promptView, setPromptView] = useState<'system' | 'mine'>('system')

  // ── Reference Docs state ──────────────────────────────────────────────
  type ReferenceDoc = {
    id: string
    name: string
    description: string | null
    file_name: string
    tags: Record<string, string[]>
    is_active: boolean
    uploaded_by_id: string
    updated_by_id: string | null
    created_at: string
    updated_at: string
  }

  const TAG_CATEGORIES = ['proposal_type', 'industry', 'client', 'engagement_size', 'region', 'technology', 'outcome'] as const

  const [referenceDocs, setReferenceDocs] = useState<ReferenceDoc[]>([])
  const [isLoadingDocs, setIsLoadingDocs] = useState(false)
  const [docSearch, setDocSearch] = useState('')
  const [showUploadPanel, setShowUploadPanel] = useState(false)
  const [editingDocId, setEditingDocId] = useState<string | null>(null)
  const [docName, setDocName] = useState('')
  const [docDescription, setDocDescription] = useState('')
  const [docFile, setDocFile] = useState<File | null>(null)
  const [docTags, setDocTags] = useState<Record<string, string[]>>({})
  const [docTagInputs, setDocTagInputs] = useState<Record<string, string>>({})
  const [isSuggestingTags, setIsSuggestingTags] = useState(false)
  const [isSavingDoc, setIsSavingDoc] = useState(false)
  // ────────────────────────────────────────────────────────────────────────

  const [inviteTemplate, setInviteTemplate] = useState<EmailTemplateData | null>(null)
  const [inviteTemplateSubject, setInviteTemplateSubject] = useState('')
  const [inviteTemplateBodyText, setInviteTemplateBodyText] = useState('')
  const [inviteTemplateBodyHtml, setInviteTemplateBodyHtml] = useState('')
  const [isLoadingInviteTemplate, setIsLoadingInviteTemplate] = useState(false)
  const [isSavingInviteTemplate, setIsSavingInviteTemplate] = useState(false)

  // ── Reference Docs helpers ──────────────────────────────────────────────
  const loadReferenceDocs = async () => {
    setIsLoadingDocs(true)
    setError(null)
    try {
      const resp = await apiClient.get<ReferenceDoc[]>('/api/reference-docs/')
      setReferenceDocs(resp)
    } catch (e: any) {
      setError(e?.message || 'Failed to load reference docs')
    } finally {
      setIsLoadingDocs(false)
    }
  }

  const startCreateDoc = () => {
    setEditingDocId(null)
    setDocName('')
    setDocDescription('')
    setDocFile(null)
    setDocTags({})
    setDocTagInputs({})
    setShowUploadPanel(true)
    setError(null)
    setNotice(null)
  }

  const startEditDoc = (doc: ReferenceDoc) => {
    setEditingDocId(doc.id)
    setDocName(doc.name)
    setDocDescription(doc.description || '')
    setDocFile(null)
    setDocTags({ ...doc.tags })
    setDocTagInputs({})
    setShowUploadPanel(true)
    setError(null)
    setNotice(null)
  }

  const suggestDocTags = async () => {
    if (!docFile) { setError('Please select a file first to suggest tags'); return }
    setIsSuggestingTags(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', docFile)
      const resp = await apiClient.postForm<{ suggested_tags: Record<string, string[]> }>(
        '/api/reference-docs/suggest-tags',
        form,
      )
      setDocTags(resp.suggested_tags || {})
      setNotice('Tags suggested — review and edit before saving')
    } catch (e: any) {
      setError(e?.message || 'Failed to suggest tags')
    } finally {
      setIsSuggestingTags(false)
    }
  }

  const addDocTag = (category: string, value: string) => {
    const v = value.trim()
    if (!v) return
    setDocTags((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []).filter((x) => x !== v), v],
    }))
    setDocTagInputs((prev) => ({ ...prev, [category]: '' }))
  }

  const removeDocTag = (category: string, value: string) => {
    setDocTags((prev) => ({ ...prev, [category]: (prev[category] || []).filter((x) => x !== value) }))
  }

  const saveDoc = async () => {
    if (!docName.trim()) { setError('Name is required'); return }
    if (!editingDocId && !docFile) { setError('Please select a file to upload'); return }
    setIsSavingDoc(true)
    setError(null)
    setNotice(null)
    try {
      if (editingDocId) {
        await apiClient.put<ReferenceDoc>(`/api/reference-docs/${editingDocId}`, {
          name: docName.trim(),
          description: docDescription.trim() || null,
          tags: docTags,
        })
        setNotice('Reference doc updated')
      } else {
        const formData = new FormData()
        formData.append('file', docFile!)
        formData.append('name', docName.trim())
        if (docDescription.trim()) formData.append('description', docDescription.trim())
        formData.append('tags', JSON.stringify(docTags))
        await apiClient.postForm<ReferenceDoc>('/api/reference-docs/', formData)
        setNotice('Reference doc uploaded')
      }
      setShowUploadPanel(false)
      await loadReferenceDocs()
    } catch (e: any) {
      setError(e?.message || 'Failed to save reference doc')
    } finally {
      setIsSavingDoc(false)
    }
  }

  const deleteDoc = async (doc: ReferenceDoc) => {
    const ok = window.confirm(`Delete reference doc "${doc.name}"?`)
    if (!ok) return
    setError(null)
    setNotice(null)
    try {
      await apiClient.delete<void>(`/api/reference-docs/${doc.id}`)
      setNotice('Reference doc deleted')
      await loadReferenceDocs()
    } catch (e: any) {
      setError(e?.message || 'Failed to delete reference doc')
    }
  }

  const filteredDocs = referenceDocs.filter((d) => {
    const q = docSearch.trim().toLowerCase()
    if (!q) return true
    const tagStr = Object.entries(d.tags).flatMap(([k, v]) => v.map((x) => `${k}:${x}`)).join(' ')
    return `${d.name} ${d.file_name} ${tagStr}`.toLowerCase().includes(q)
  })
  // ────────────────────────────────────────────────────────────────────────

  const renderInviteTemplate = (raw: string) => {
    const sampleInviteUrl = 'https://adya.aditiai.solutions/accept-invite?token=EXAMPLE_TOKEN'
    const sampleRecipient = 'new.user@example.com'
    return (raw || '')
      .replaceAll('{{invite_url}}', sampleInviteUrl)
      .replaceAll('{{recipient_email}}', sampleRecipient)
  }

  const openInviteModal = (email: string, url: string) => {
    setInviteModalEmail(email)
    setInviteUrl(url)
    setInviteModalOpen(true)
  }

  const generateInviteForUser = async (u: AdminUser) => {
    setError(null)
    setNotice(null)
    setIsGeneratingInvite(true)
    try {
      const resp = await apiClient.generateInviteLink(u.id)
      openInviteModal(u.email, resp.invite_url)
    } catch (e: any) {
      setError(e?.message || 'Failed to generate invite link')
    } finally {
      setIsGeneratingInvite(false)
    }
  }

  useEffect(() => {
    if (!isAdmin) {
      setActiveTab('prompts')
      setPromptView('system')
    }
  }, [isAdmin])

  const loadInviteEmailTemplate = async () => {
    if (!isAdmin) return
    setIsLoadingInviteTemplate(true)
    setError(null)
    setNotice(null)
    try {
      const tpl = await apiClient.getEmailTemplate('invite')
      setInviteTemplate(tpl)
      setInviteTemplateSubject(tpl.subject)
      setInviteTemplateBodyText(tpl.body_text)
      setInviteTemplateBodyHtml(tpl.body_html)
    } catch (e: any) {
      setError(e?.message || 'Failed to load invite email template')
    } finally {
      setIsLoadingInviteTemplate(false)
    }
  }

  const saveInviteEmailTemplate = async () => {
    if (!isAdmin) return
    setIsSavingInviteTemplate(true)
    setError(null)
    setNotice(null)
    try {
      const tpl = await apiClient.updateEmailTemplate('invite', {
        subject: inviteTemplateSubject,
        body_text: inviteTemplateBodyText,
        body_html: inviteTemplateBodyHtml,
      })
      setInviteTemplate(tpl)
      setNotice('Invite email template saved')
    } catch (e: any) {
      setError(e?.message || 'Failed to save invite email template')
    } finally {
      setIsSavingInviteTemplate(false)
    }
  }

  const loadUsers = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      })
      if (search.trim()) params.set('search', search.trim())
      if (roleFilter !== 'all') params.set('role', roleFilter)
      if (activeFilter === 'active') params.set('is_active', 'true')
      if (activeFilter === 'inactive') params.set('is_active', 'false')

      const resp = await apiClient.get<AdminUserListResponse>(`/api/admin/users?${params.toString()}`)
      setUsers(resp.users)
      setTotalPages(resp.total_pages)
      setDrafts((prev) => {
        const next = { ...prev }
        for (const u of resp.users) {
          if (!next[u.id]) {
            next[u.id] = { role: u.role, is_active: u.is_active }
          }
        }
        return next
      })
    } catch (e: any) {
      setError(e?.message || 'Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isAdmin) return
    loadUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, page])

  useEffect(() => {
    if (!isAdmin) return
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, activeFilter])

  useEffect(() => {
    if (!isAdmin) return
    loadUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, activeFilter])

  const roleOptions: UserRole[] = useMemo(() => ['admin', 'manager', 'member'], [])

  const updateDraft = (userId: string, patch: Partial<Pick<AdminUser, 'role' | 'is_active'>>) => {
    setDrafts((prev) => ({
      ...prev,
      [userId]: {
        role: prev[userId]?.role ?? 'member',
        is_active: prev[userId]?.is_active ?? true,
        ...patch,
      },
    }))
  }

  const isDirty = (u: AdminUser) => {
    const d = drafts[u.id]
    if (!d) return false
    return d.role !== u.role || d.is_active !== u.is_active
  }

  const agentOptions = useMemo(
    () => [
      'project_intake',
      'solution_architecture',
      'scope_decomposition',
      'effort_estimation',
      'proposal_generation',
      'success_assessment',
      'proposal_review',
    ],
    []
  )

  const loadSystemPrompts = async () => {
    setIsLoadingPrompts(true)
    setError(null)
    setNotice(null)

    try {
      const params = new URLSearchParams()
      if (promptAgentFilter.trim()) params.set('agent_name', promptAgentFilter.trim())
      if (promptIndustryFilter.trim()) params.set('industry', promptIndustryFilter.trim())
      if (promptProposalTypeFilter.trim()) params.set('proposal_type', promptProposalTypeFilter.trim())

      const url = params.toString() ? `/api/prompts/system?${params.toString()}` : '/api/prompts/system'
      const resp = await apiClient.get<PromptTemplate[]>(url)

      const q = promptSearch.trim().toLowerCase()
      const filtered = q
        ? resp.filter((p) => {
            const hay = `${p.name} ${p.agent_name} ${p.industry || ''} ${p.proposal_type || ''}`.toLowerCase()
            return hay.includes(q)
          })
        : resp

      setSystemPrompts(filtered)
    } catch (e: any) {
      setError(e?.message || 'Failed to load system prompts')
    } finally {
      setIsLoadingPrompts(false)
    }
  }

  const loadMyPrompts = async () => {
    setIsLoadingPrompts(true)
    setError(null)
    setNotice(null)

    try {
      const params = new URLSearchParams()
      if (promptAgentFilter.trim()) params.set('agent_name', promptAgentFilter.trim())
      if (promptIndustryFilter.trim()) params.set('industry', promptIndustryFilter.trim())
      if (promptProposalTypeFilter.trim()) params.set('proposal_type', promptProposalTypeFilter.trim())

      const url = params.toString() ? `/api/prompts/me?${params.toString()}` : '/api/prompts/me'
      const resp = await apiClient.get<PromptTemplate[]>(url)

      const q = promptSearch.trim().toLowerCase()
      const filtered = q
        ? resp.filter((p) => {
            const hay = `${p.name} ${p.agent_name} ${p.industry || ''} ${p.proposal_type || ''}`.toLowerCase()
            return hay.includes(q)
          })
        : resp

      setSystemPrompts(filtered)
    } catch (e: any) {
      setError(e?.message || 'Failed to load your prompts')
    } finally {
      setIsLoadingPrompts(false)
    }
  }

  const seedSystemPrompts = async () => {
    setIsSeeding(true)
    setError(null)
    setNotice(null)
    try {
      const resp = await apiClient.post<SeedPromptsResponse>('/api/prompts/system/seed-from-files', {})
      setNotice(`Seeded prompts. Created: ${resp.created}, updated: ${resp.updated}.`)
      await loadSystemPrompts()
    } catch (e: any) {
      setError(e?.message || 'Failed to seed prompts')
    } finally {
      setIsSeeding(false)
    }
  }

  const startCreatePrompt = () => {
    setEditingPromptId(null)
    setPromptName('')
    setPromptAgentName('project_intake')
    setPromptIndustry('')
    setPromptProposalType('')
    setPromptContent('')
    setNotice(null)
    setError(null)
  }

  const startEditPrompt = (p: PromptTemplate) => {
    setEditingPromptId(p.id)
    setPromptName(p.name)
    setPromptAgentName(p.agent_name)
    setPromptIndustry(p.industry || '')
    setPromptProposalType(p.proposal_type || '')
    setPromptContent(p.content)
    setNotice(null)
    setError(null)
  }

  const savePrompt = async () => {
    if (!promptName.trim() || !promptAgentName.trim() || !promptContent.trim()) {
      setError('Name, agent, and content are required')
      return
    }

    setError(null)
    setNotice(null)
    try {
      if (isAdmin) {
        if (editingPromptId) {
          await apiClient.patch<PromptTemplate>(`/api/prompts/system/${editingPromptId}`, {
            name: promptName.trim(),
            industry: promptIndustry.trim() || null,
            proposal_type: promptProposalType.trim() || null,
            content: promptContent,
          })
          setNotice('Prompt updated')
        } else {
          await apiClient.post<PromptTemplate>('/api/prompts/system', {
            name: promptName.trim(),
            agent_name: promptAgentName.trim(),
            industry: promptIndustry.trim() || null,
            proposal_type: promptProposalType.trim() || null,
            content: promptContent,
          })
          setNotice('Prompt created')
        }

        await loadSystemPrompts()
      } else {
        if (editingPromptId) {
          await apiClient.patch<PromptTemplate>(`/api/prompts/me/${editingPromptId}`, {
            name: promptName.trim(),
            industry: promptIndustry.trim() || null,
            proposal_type: promptProposalType.trim() || null,
            content: promptContent,
          })
          setNotice('Prompt updated')
        } else {
          await apiClient.post<PromptTemplate>('/api/prompts/me', {
            name: promptName.trim(),
            agent_name: promptAgentName.trim(),
            industry: promptIndustry.trim() || null,
            proposal_type: promptProposalType.trim() || null,
            content: promptContent,
          })
          setNotice('Prompt created')
        }

        await loadMyPrompts()
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to save prompt')
    }
  }

  const deletePrompt = async (p: PromptTemplate) => {
    const ok = window.confirm(`Delete prompt "${p.name}"?`)
    if (!ok) return

    setError(null)
    setNotice(null)
    try {
      if (isAdmin) {
        await apiClient.delete<void>(`/api/prompts/system/${p.id}`)
      } else {
        await apiClient.delete<void>(`/api/prompts/me/${p.id}`)
      }
      setNotice('Prompt deleted')
      if (editingPromptId === p.id) {
        startCreatePrompt()
      }
      if (isAdmin) {
        await loadSystemPrompts()
      } else {
        await loadMyPrompts()
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to delete prompt')
    }
  }

  const saveUser = async (u: AdminUser) => {
    const d = drafts[u.id]
    if (!d) return

    setSavingUserIds((prev) => new Set(prev).add(u.id))
    setError(null)

    try {
      const updated = await apiClient.patch<AdminUser>(`/api/admin/users/${u.id}`, d)
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)))
      setDrafts((prev) => ({
        ...prev,
        [u.id]: { role: updated.role, is_active: updated.is_active },
      }))
    } catch (e: any) {
      setError(e?.message || 'Failed to update user')
    } finally {
      setSavingUserIds((prev) => {
        const next = new Set(prev)
        next.delete(u.id)
        return next
      })
    }
  }

  const createUser = async () => {
    if (!newEmail.trim()) {
      setError('Email is required')
      return
    }

    setIsCreating(true)
    setError(null)
    try {
      await apiClient.post<AdminUser>('/api/admin/users', {
        email: newEmail.trim(),
        role: newRole,
        is_active: newIsActive,
      })

      setNewEmail('')
      setNewRole('member')
      setNewIsActive(false)

      await loadUsers()
    } catch (e: any) {
      setError(e?.message || 'Failed to create user')
    } finally {
      setIsCreating(false)
    }
  }

  const deactivateUser = async (u: AdminUser) => {
    const isSelf = user?.id === u.id
    if (isSelf) return

    const ok = window.confirm(`Deactivate ${u.email}?`)
    if (!ok) return

    setError(null)
    try {
      await apiClient.delete<void>(`/api/admin/users/${u.id}`)
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_active: false } : x)))
      setDrafts((prev) => ({
        ...prev,
        [u.id]: { role: prev[u.id]?.role ?? u.role, is_active: false },
      }))
    } catch (e: any) {
      setError(e?.message || 'Failed to deactivate user')
    }
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, var(--cream), var(--sand))' }}>
      <div className="container mx-auto px-4 py-6">
        {inviteModalOpen && (
          <div
            className="fixed inset-0 flex items-center justify-center px-4"
            style={{ background: 'rgba(0,0,0,0.45)', zIndex: 50 }}
            onClick={() => setInviteModalOpen(false)}
          >
            <div
              className="w-full max-w-lg rounded-lg border p-6"
              style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold" style={{ color: 'var(--charcoal)', fontFamily: 'Crimson Text, Georgia, serif' }}>
                    Invite link
                  </div>
                  <div className="text-sm" style={{ color: 'var(--charcoal-light)', fontFamily: 'DM Sans, sans-serif' }}>
                    {inviteModalEmail}
                  </div>
                </div>
                <button
                  onClick={() => setInviteModalOpen(false)}
                  className="px-3 py-2 rounded-md"
                  style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--charcoal)' }}
                >
                  Close
                </button>
              </div>

              <div className="mt-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                  URL
                </label>
                <input
                  value={inviteUrl}
                  readOnly
                  className="w-full px-3 py-2 rounded-md border"
                  style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                />
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(inviteUrl)
                        setNotice('Invite link copied')
                      } catch {
                        setError('Failed to copy invite link')
                      }
                    }}
                    className="px-4 py-2 rounded-md"
                    style={{ background: 'var(--terracotta)', color: 'white' }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 style={{ fontFamily: 'Crimson Text, Georgia, serif', fontSize: '2rem', fontWeight: 600, color: 'var(--charcoal)' }}>
              {isAdmin ? 'Admin' : 'Prompts'}
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--charcoal-light)' }}>
              {isAdmin ? 'User management and role-based access control' : 'View system prompts and manage your own prompts'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (isAdmin) {
                  if (activeTab === 'users') {
                    void loadUsers()
                    return
                  }
                  if (activeTab === 'prompts') {
                    void loadSystemPrompts()
                    return
                  }
                  if (activeTab === 'emailTemplates') {
                    void loadInviteEmailTemplate()
                    return
                  }
                }
                void (promptView === 'system' ? loadSystemPrompts() : loadMyPrompts())
              }}
              className="px-4 py-2 rounded-md"
              style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)', fontFamily: 'DM Sans, sans-serif' }}
              disabled={isLoading}
            >
              Refresh
            </button>
            <Link
              to="/"
              className="px-4 py-2 rounded-md"
              style={{ background: 'var(--terracotta)', color: 'white', fontFamily: 'DM Sans, sans-serif' }}
            >
              Back
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-md" style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#991b1b' }}>
            {error}
          </div>
        )}

        {notice && (
          <div className="mb-4 p-4 rounded-md" style={{ background: 'rgba(34, 197, 94, 0.08)', color: '#15803d' }}>
            {notice}
          </div>
        )}

        <div className="flex items-center gap-2 mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('users')}
              className="px-4 py-2 rounded-md text-sm font-medium"
              style={{
                background: activeTab === 'users' ? 'var(--terracotta)' : 'rgba(200, 88, 53, 0.08)',
                color: activeTab === 'users' ? 'white' : 'var(--terracotta)',
              }}
            >
              Users
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('roles')}
              className="px-4 py-2 rounded-md text-sm font-medium"
              style={{
                background: activeTab === 'roles' ? 'var(--terracotta)' : 'rgba(200, 88, 53, 0.08)',
                color: activeTab === 'roles' ? 'white' : 'var(--terracotta)',
              }}
            >
              Roles & Permissions
            </button>
          )}

          <button
            onClick={() => {
              setActiveTab('prompts')
              startCreatePrompt()
              if (isAdmin) {
                void loadSystemPrompts()
              } else {
                void loadMyPrompts()
              }
            }}
            className="px-4 py-2 rounded-md text-sm font-medium"
            style={{
              background: activeTab === 'prompts' ? 'var(--terracotta)' : 'rgba(200, 88, 53, 0.08)',
              color: activeTab === 'prompts' ? 'white' : 'var(--terracotta)',
            }}
          >
            Prompts
          </button>

          {isAdmin && (
            <button
              onClick={() => {
                setActiveTab('emailTemplates')
                void loadInviteEmailTemplate()
              }}
              className="px-4 py-2 rounded-md text-sm font-medium"
              style={{
                background: activeTab === 'emailTemplates' ? 'var(--terracotta)' : 'rgba(200, 88, 53, 0.08)',
                color: activeTab === 'emailTemplates' ? 'white' : 'var(--terracotta)',
              }}
            >
              Email Templates
            </button>
          )}

          {isAdminOrManager && (
            <button
              onClick={() => {
                setActiveTab('referenceDocs')
                setShowUploadPanel(false)
                void loadReferenceDocs()
              }}
              className="px-4 py-2 rounded-md text-sm font-medium"
              style={{
                background: activeTab === 'referenceDocs' ? 'var(--terracotta)' : 'rgba(200, 88, 53, 0.08)',
                color: activeTab === 'referenceDocs' ? 'white' : 'var(--terracotta)',
              }}
            >
              Reference Docs
            </button>
          )}
        </div>

        {activeTab === 'referenceDocs' && isAdminOrManager && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: 'var(--charcoal)' }}>
                    Reference Docs
                  </h2>
                  <div className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                    Upload past proposals as reference material for RAG-powered proposal generation
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => void loadReferenceDocs()}
                    disabled={isLoadingDocs}
                    className="px-4 py-2 rounded-md"
                    style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)', fontFamily: 'DM Sans, sans-serif' }}
                  >
                    {isLoadingDocs ? 'Loading…' : 'Refresh'}
                  </button>
                  <button
                    onClick={startCreateDoc}
                    className="px-4 py-2 rounded-md font-medium"
                    style={{ background: 'var(--terracotta)', color: 'white', fontFamily: 'DM Sans, sans-serif' }}
                  >
                    + Upload Doc
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="mb-4">
                <input
                  value={docSearch}
                  onChange={(e) => setDocSearch(e.target.value)}
                  placeholder="Search by name or tag…"
                  className="w-full px-3 py-2 rounded-md border"
                  style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)', fontFamily: 'DM Sans, sans-serif' }}
                />
              </div>

              {/* Upload / Edit panel */}
              {showUploadPanel && (
                <div className="rounded-md border p-5 mb-5" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm font-semibold" style={{ color: 'var(--charcoal)' }}>
                      {editingDocId ? 'Edit Reference Doc' : 'Upload Reference Doc'}
                    </div>
                    <button
                      onClick={() => setShowUploadPanel(false)}
                      className="px-3 py-1 rounded-md text-sm"
                      style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--charcoal-light)' }}
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>Name *</label>
                      <input
                        value={docName}
                        onChange={(e) => setDocName(e.target.value)}
                        className="w-full px-3 py-2 rounded-md border"
                        style={{ borderColor: 'var(--sand)', background: 'white' }}
                        placeholder="Q4 Retail SOW"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>Description</label>
                      <input
                        value={docDescription}
                        onChange={(e) => setDocDescription(e.target.value)}
                        className="w-full px-3 py-2 rounded-md border"
                        style={{ borderColor: 'var(--sand)', background: 'white' }}
                        placeholder="(optional)"
                      />
                    </div>
                  </div>

                  {!editingDocId && (
                    <div className="mb-4">
                      <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>File (.txt, .md, .pdf, .docx) *</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept=".txt,.md,.pdf,.docx"
                          onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                          className="flex-1 px-3 py-2 rounded-md border"
                          style={{ borderColor: 'var(--sand)', background: 'white' }}
                        />
                        <button
                          onClick={() => void suggestDocTags()}
                          disabled={isSuggestingTags || !docFile}
                          className="px-4 py-2 rounded-md whitespace-nowrap"
                          style={{
                            background: isSuggestingTags || !docFile ? 'rgba(200, 88, 53, 0.08)' : 'var(--terracotta)',
                            color: isSuggestingTags || !docFile ? 'var(--terracotta)' : 'white',
                            fontFamily: 'DM Sans, sans-serif',
                          }}
                        >
                          {isSuggestingTags ? 'Suggesting…' : 'Suggest Tags'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Tag editor */}
                  <div className="mb-4">
                    <div className="text-xs font-semibold mb-2" style={{ color: 'var(--charcoal)' }}>Tags</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {TAG_CATEGORIES.map((cat) => (
                        <div key={cat}>
                          <label className="block text-xs mb-1 capitalize" style={{ color: 'var(--charcoal-light)' }}>
                            {cat.replace(/_/g, ' ')}
                          </label>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(docTags[cat] || []).map((v) => (
                              <span
                                key={v}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                                style={{ background: 'rgba(200, 88, 53, 0.12)', color: 'var(--terracotta)' }}
                              >
                                {v}
                                <button
                                  onClick={() => removeDocTag(cat, v)}
                                  style={{ lineHeight: 1, fontWeight: 700 }}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-1">
                            <input
                              value={docTagInputs[cat] || ''}
                              onChange={(e) => setDocTagInputs((prev) => ({ ...prev, [cat]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDocTag(cat, docTagInputs[cat] || '') } }}
                              className="flex-1 px-2 py-1 rounded-md border text-xs"
                              style={{ borderColor: 'var(--sand)', background: 'white' }}
                              placeholder="Add value + Enter"
                            />
                            <button
                              onClick={() => addDocTag(cat, docTagInputs[cat] || '')}
                              className="px-2 py-1 rounded-md text-xs"
                              style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)' }}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => void saveDoc()}
                      disabled={isSavingDoc}
                      className="px-5 py-2 rounded-md font-medium"
                      style={{
                        background: isSavingDoc ? 'rgba(200, 88, 53, 0.08)' : 'var(--terracotta)',
                        color: isSavingDoc ? 'var(--terracotta)' : 'white',
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                    >
                      {isSavingDoc ? 'Saving…' : editingDocId ? 'Save Changes' : 'Upload'}
                    </button>
                  </div>
                </div>
              )}

              {/* Docs table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  <thead>
                    <tr style={{ color: 'var(--charcoal-light)' }}>
                      <th className="text-left py-2">Name</th>
                      <th className="text-left py-2">Tags</th>
                      <th className="text-left py-2">File</th>
                      <th className="text-left py-2">Updated</th>
                      <th className="text-right py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocs.map((doc) => (
                      <tr key={doc.id} className="border-t" style={{ borderColor: 'var(--sand)' }}>
                        <td className="py-3 pr-4" style={{ color: 'var(--charcoal)' }}>
                          <div className="font-medium">{doc.name}</div>
                          {doc.description && (
                            <div className="text-xs" style={{ color: 'var(--charcoal-light)' }}>{doc.description}</div>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(doc.tags).flatMap(([cat, vals]) =>
                              vals.map((v) => (
                                <span
                                  key={`${cat}:${v}`}
                                  className="px-2 py-0.5 rounded-full text-xs"
                                  style={{ background: 'rgba(200, 88, 53, 0.12)', color: 'var(--terracotta)' }}
                                >
                                  {v}
                                </span>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="py-3 pr-4" style={{ color: 'var(--charcoal-light)' }}>
                          {doc.file_name}
                        </td>
                        <td className="py-3 pr-4" style={{ color: 'var(--charcoal-light)' }}>
                          {new Date(doc.updated_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => startEditDoc(doc)}
                              className="px-3 py-2 rounded-md"
                              style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)' }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => void deleteDoc(doc)}
                              className="px-3 py-2 rounded-md"
                              style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#991b1b' }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredDocs.length === 0 && !isLoadingDocs && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center" style={{ color: 'var(--charcoal-light)' }}>
                          {docSearch ? 'No docs match your search.' : 'No reference docs yet. Upload one to get started.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 text-sm" style={{ color: 'var(--charcoal-light)', fontFamily: 'DM Sans, sans-serif' }}>
                {isLoadingDocs ? 'Loading…' : `${filteredDocs.length} doc${filteredDocs.length !== 1 ? 's' : ''}`}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'users' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold" style={{ color: 'var(--charcoal)' }}>
                  Users
                </h2>
                <div className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                  {isLoading ? 'Loading…' : `${users.length} users loaded`}
                </div>
              </div>

            <div className="rounded-md border p-4 mb-4" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
              <div className="text-sm font-semibold mb-3" style={{ color: 'var(--charcoal)' }}>
                Add User
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-3">
                  <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                    Email
                  </label>
                  <input
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border"
                    style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="md:col-span-3">
                  <div className="text-xs" style={{ color: 'var(--charcoal-light)' }}>
                    The user will receive an email link to set their password.
                  </div>
                </div>
                <div className="md:col-span-4">
                  <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                    Role
                  </label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as UserRole)}
                    className="w-full px-3 py-2 rounded-md border"
                    style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                  >
                    {roleOptions.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newIsActive}
                    onChange={(e) => setNewIsActive(e.target.checked)}
                    disabled
                  />
                  <span className="text-xs" style={{ color: 'var(--charcoal-light)' }}>
                    Active
                  </span>
                </div>
                <div className="md:col-span-12 flex justify-end">
                  <button
                    onClick={createUser}
                    disabled={isCreating}
                    className="px-4 py-2 rounded-md font-medium"
                    style={{
                      background: isCreating ? 'rgba(200, 88, 53, 0.08)' : 'var(--terracotta)',
                      color: isCreating ? 'var(--terracotta)' : 'white',
                    }}
                  >
                    {isCreating ? 'Sending invite…' : 'Invite User'}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
              <div className="flex-1">
                <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                  Search
                </label>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border"
                  style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                  placeholder="Search by email or name"
                />
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                  Role
                </label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value as any)}
                  className="px-3 py-2 rounded-md border"
                  style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                >
                  <option value="all">All</option>
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                  Status
                </label>
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value as any)}
                  className="px-3 py-2 rounded-md border"
                  style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                <thead>
                  <tr style={{ color: 'var(--charcoal-light)' }}>
                    <th className="text-left py-2">Name</th>
                    <th className="text-left py-2">Email</th>
                    <th className="text-left py-2">Role</th>
                    <th className="text-left py-2">Active</th>
                    <th className="text-right py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const draft = drafts[u.id] || { role: u.role, is_active: u.is_active }
                    const saving = savingUserIds.has(u.id)
                    const isSelf = user?.id === u.id
                    const roleDisabled = isSelf
                    const activeDisabled = isSelf
                    const canInvite = !isSelf && !u.is_active

                    return (
                      <tr key={u.id} className="border-t" style={{ borderColor: 'var(--sand)' }}>
                        <td className="py-3 pr-4" style={{ color: 'var(--charcoal)' }}>
                          {u.full_name}
                        </td>
                        <td className="py-3 pr-4" style={{ color: 'var(--charcoal-light)' }}>
                          {u.email}
                        </td>
                        <td className="py-3 pr-4">
                          <select
                            value={draft.role}
                            onChange={(e) => updateDraft(u.id, { role: e.target.value as UserRole })}
                            disabled={roleDisabled}
                            className="px-3 py-2 rounded-md border"
                            style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                          >
                            {roleOptions.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-3 pr-4">
                          <input
                            type="checkbox"
                            checked={draft.is_active}
                            onChange={(e) => updateDraft(u.id, { is_active: e.target.checked })}
                            disabled={activeDisabled}
                          />
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => saveUser(u)}
                              disabled={saving || !isDirty(u) || (isSelf && (draft.role !== u.role || draft.is_active !== u.is_active))}
                              className="px-4 py-2 rounded-md font-medium"
                              style={{
                                background: saving || !isDirty(u) ? 'rgba(200, 88, 53, 0.08)' : 'var(--terracotta)',
                                color: saving || !isDirty(u) ? 'var(--terracotta)' : 'white',
                              }}
                            >
                              {saving ? 'Saving…' : isDirty(u) ? 'Save' : 'Saved'}
                            </button>
                            {canInvite && (
                              <button
                                onClick={() => {
                                  if (u.invite_url) {
                                    openInviteModal(u.email, u.invite_url)
                                  } else {
                                    void generateInviteForUser(u)
                                  }
                                }}
                                disabled={isGeneratingInvite}
                                className="px-3 py-2 rounded-md"
                                style={{
                                  background: 'rgba(200, 88, 53, 0.08)',
                                  color: 'var(--terracotta)',
                                  opacity: isGeneratingInvite ? 0.6 : 1,
                                }}
                              >
                                Invite link
                              </button>
                            )}
                            <button
                              onClick={() => deactivateUser(u)}
                              disabled={isSelf || !u.is_active}
                              className="px-3 py-2 rounded-md"
                              style={{
                                background: 'rgba(239, 68, 68, 0.08)',
                                color: '#991b1b',
                                opacity: isSelf || !u.is_active ? 0.5 : 1,
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              <div className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                Page {page} of {Math.max(totalPages, 1)}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isLoading}
                  className="px-3 py-2 rounded-md"
                  style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)' }}
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => (totalPages ? Math.min(totalPages, p + 1) : p + 1))}
                  disabled={(totalPages && page >= totalPages) || isLoading}
                  className="px-3 py-2 rounded-md"
                  style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)' }}
                >
                  Next
                </button>
              </div>
            </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'emailTemplates' && isAdmin && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold" style={{ color: 'var(--charcoal)' }}>
                  Invite Email Template
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => void loadInviteEmailTemplate()}
                    className="px-4 py-2 rounded-md"
                    style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)' }}
                    disabled={isLoadingInviteTemplate}
                  >
                    {isLoadingInviteTemplate ? 'Loading…' : 'Reload'}
                  </button>
                  <button
                    onClick={() => void saveInviteEmailTemplate()}
                    className="px-4 py-2 rounded-md"
                    style={{ background: 'var(--terracotta)', color: 'white' }}
                    disabled={isSavingInviteTemplate}
                  >
                    {isSavingInviteTemplate ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              <div className="text-sm mb-4" style={{ color: 'var(--charcoal-light)' }}>
                Supported placeholders:
                <span className="font-mono">{'{{invite_url}}'}</span>
                and
                <span className="font-mono">{'{{recipient_email}}'}</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                    Subject
                  </label>
                  <input
                    value={inviteTemplateSubject}
                    onChange={(e) => setInviteTemplateSubject(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border mb-4"
                    style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                  />

                  <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                    Body (Text)
                  </label>
                  <textarea
                    value={inviteTemplateBodyText}
                    onChange={(e) => setInviteTemplateBodyText(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border font-mono text-sm"
                    style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)', minHeight: 220 }}
                  />

                  <label className="block text-xs mt-4 mb-1" style={{ color: 'var(--charcoal-light)' }}>
                    Body (HTML)
                  </label>
                  <textarea
                    value={inviteTemplateBodyHtml}
                    onChange={(e) => setInviteTemplateBodyHtml(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border font-mono text-sm"
                    style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)', minHeight: 260 }}
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold mb-2" style={{ color: 'var(--charcoal)' }}>
                    Preview
                  </div>

                  <div className="rounded-md border p-4 mb-4" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
                    <div className="text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                      Rendered subject
                    </div>
                    <div className="font-medium" style={{ color: 'var(--charcoal)' }}>
                      {renderInviteTemplate(inviteTemplateSubject)}
                    </div>
                  </div>

                  <div className="rounded-md border p-4 mb-4" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
                    <div className="text-xs mb-2" style={{ color: 'var(--charcoal-light)' }}>
                      Rendered text body
                    </div>
                    <pre className="whitespace-pre-wrap text-sm" style={{ color: 'var(--charcoal)' }}>
                      {renderInviteTemplate(inviteTemplateBodyText)}
                    </pre>
                  </div>

                  <div className="rounded-md border overflow-hidden" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
                    <div className="p-3 border-b" style={{ borderColor: 'var(--sand)', color: 'var(--charcoal-light)', fontSize: 12 }}>
                      Rendered HTML body
                    </div>
                    <iframe
                      title="Invite email HTML preview"
                      className="w-full"
                      style={{ height: 420, border: 0, background: 'white' }}
                      srcDoc={renderInviteTemplate(inviteTemplateBodyHtml)}
                    />
                  </div>
                </div>
              </div>

              {inviteTemplate?.updated_at && (
                <div className="text-xs mt-4" style={{ color: 'var(--charcoal-light)' }}>
                  Last saved: {new Date(inviteTemplate.updated_at).toLocaleString()}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'roles' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold" style={{ color: 'var(--charcoal)' }}>
                  Roles & Permissions
                </h2>
              </div>

              <div className="rounded-md border p-4" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
                <div className="text-sm" style={{ color: 'var(--charcoal-light)', fontFamily: 'DM Sans, sans-serif' }}>
                  This is a display-only overview. Permissions are enforced in backend code.
                </div>

                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    <thead>
                      <tr style={{ color: 'var(--charcoal-light)' }}>
                        <th className="text-left py-2">Capability</th>
                        <th className="text-center py-2">Member</th>
                        <th className="text-center py-2">Manager</th>
                        <th className="text-center py-2">Admin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          key: 'projects',
                          label: 'Projects',
                          desc: 'Create and work on projects you have access to',
                          member: true,
                          manager: true,
                          admin: true,
                        },
                        {
                          key: 'org_visibility',
                          label: 'Org-wide project visibility',
                          desc: 'See projects across the organization',
                          member: false,
                          manager: true,
                          admin: true,
                        },
                        {
                          key: 'user_mgmt',
                          label: 'User management',
                          desc: 'Create, deactivate, and change roles',
                          member: false,
                          manager: false,
                          admin: true,
                        },
                        {
                          key: 'system_prompts',
                          label: 'System prompts',
                          desc: 'Manage default prompts (coming next)',
                          member: false,
                          manager: false,
                          admin: true,
                        },
                      ].map((row) => (
                        <tr key={row.key} className="border-t" style={{ borderColor: 'var(--sand)' }}>
                          <td className="py-3 pr-4">
                            <div style={{ color: 'var(--charcoal)' }}>{row.label}</div>
                            <div className="text-xs" style={{ color: 'var(--charcoal-light)' }}>
                              {row.desc}
                            </div>
                          </td>
                          <td className="py-3 text-center" style={{ color: row.member ? '#15803d' : '#991b1b' }}>
                            {row.member ? 'Yes' : 'No'}
                          </td>
                          <td className="py-3 text-center" style={{ color: row.manager ? '#15803d' : '#991b1b' }}>
                            {row.manager ? 'Yes' : 'No'}
                          </td>
                          <td className="py-3 text-center" style={{ color: row.admin ? '#15803d' : '#991b1b' }}>
                            {row.admin ? 'Yes' : 'No'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'prompts' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: 'var(--charcoal)' }}>
                    {isAdmin ? 'System Prompts' : promptView === 'system' ? 'System Prompts' : 'Your Prompts'}
                  </h2>
                  <div className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                    {isAdmin
                      ? 'Manage default agent prompts (admins only)'
                      : promptView === 'system'
                        ? 'View default agent prompts (read-only)'
                        : 'Create and manage prompts for your account'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!isAdmin && (
                    <>
                      <button
                        onClick={() => {
                          setPromptView('system')
                          void loadSystemPrompts()
                        }}
                        className="px-4 py-2 rounded-md"
                        style={{
                          background: promptView === 'system' ? 'var(--terracotta)' : 'rgba(200, 88, 53, 0.08)',
                          color: promptView === 'system' ? 'white' : 'var(--terracotta)',
                        }}
                      >
                        System
                      </button>
                      <button
                        onClick={() => {
                          setPromptView('mine')
                          startCreatePrompt()
                          void loadMyPrompts()
                        }}
                        className="px-4 py-2 rounded-md"
                        style={{
                          background: promptView === 'mine' ? 'var(--terracotta)' : 'rgba(200, 88, 53, 0.08)',
                          color: promptView === 'mine' ? 'white' : 'var(--terracotta)',
                        }}
                      >
                        Mine
                      </button>
                    </>
                  )}
                  <button
                    onClick={
                      isAdmin
                        ? loadSystemPrompts
                        : promptView === 'system'
                          ? loadSystemPrompts
                          : loadMyPrompts
                    }
                    disabled={isLoadingPrompts}
                    className="px-4 py-2 rounded-md"
                    style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)' }}
                  >
                    Refresh
                  </button>
                  {isAdmin && (
                    <button
                      onClick={seedSystemPrompts}
                      disabled={isSeeding}
                      className="px-4 py-2 rounded-md"
                      style={{ background: 'var(--terracotta)', color: 'white' }}
                    >
                      {isSeeding ? 'Seeding…' : 'Seed from files'}
                    </button>
                  )}
                </div>
              </div>

              <div className="rounded-md border p-4 mb-4" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                  <div className="md:col-span-4">
                    <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                      Search
                    </label>
                    <input
                      value={promptSearch}
                      onChange={(e) => setPromptSearch(e.target.value)}
                      className="w-full px-3 py-2 rounded-md border"
                      style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                      placeholder="Search name/agent/industry/type"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                      Agent
                    </label>
                    <select
                      value={promptAgentFilter}
                      onChange={(e) => setPromptAgentFilter(e.target.value)}
                      className="w-full px-3 py-2 rounded-md border"
                      style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                    >
                      <option value="">All</option>
                      {agentOptions.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                      Industry
                    </label>
                    <input
                      value={promptIndustryFilter}
                      onChange={(e) => setPromptIndustryFilter(e.target.value)}
                      className="w-full px-3 py-2 rounded-md border"
                      style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                      placeholder="ecommerce"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                      Proposal type
                    </label>
                    <input
                      value={promptProposalTypeFilter}
                      onChange={(e) => setPromptProposalTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 rounded-md border"
                      style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                      placeholder="milestones"
                    />
                  </div>

                  <div className="md:col-span-1 flex justify-end">
                    <button
                      onClick={
                        isAdmin
                          ? loadSystemPrompts
                          : promptView === 'system'
                            ? loadSystemPrompts
                            : loadMyPrompts
                      }
                      className="px-4 py-2 rounded-md"
                      style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)' }}
                      disabled={isLoadingPrompts}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>

              {(!isAdmin && promptView === 'system') ? null : (
                <>
                  <div className="rounded-md border p-4 mb-4" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold" style={{ color: 'var(--charcoal)' }}>
                        {editingPromptId ? 'Edit prompt' : 'Create prompt'}
                      </div>
                      <button
                        onClick={startCreatePrompt}
                        className="px-3 py-2 rounded-md"
                        style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)' }}
                      >
                        New
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                      <div className="md:col-span-3">
                        <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                          Name
                        </label>
                        <input
                          value={promptName}
                          onChange={(e) => setPromptName(e.target.value)}
                          className="w-full px-3 py-2 rounded-md border"
                          style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                          placeholder="project_intake • base"
                        />
                      </div>

                      <div className="md:col-span-3">
                        <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                          Agent
                        </label>
                        <select
                          value={promptAgentName}
                          onChange={(e) => setPromptAgentName(e.target.value)}
                          className="w-full px-3 py-2 rounded-md border"
                          style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                        >
                          {agentOptions.map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                          Industry
                        </label>
                        <input
                          value={promptIndustry}
                          onChange={(e) => setPromptIndustry(e.target.value)}
                          className="w-full px-3 py-2 rounded-md border"
                          style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                          placeholder="(optional)"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                          Proposal type
                        </label>
                        <input
                          value={promptProposalType}
                          onChange={(e) => setPromptProposalType(e.target.value)}
                          className="w-full px-3 py-2 rounded-md border"
                          style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}
                          placeholder="(optional)"
                        />
                      </div>

                      <div className="md:col-span-2 flex justify-end">
                        <button
                          onClick={savePrompt}
                          className="px-4 py-2 rounded-md font-medium"
                          style={{ background: 'var(--terracotta)', color: 'white' }}
                        >
                          {editingPromptId ? 'Save' : 'Create'}
                        </button>
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="block text-xs mb-1" style={{ color: 'var(--charcoal-light)' }}>
                        Prompt content
                      </label>
                      <textarea
                        value={promptContent}
                        onChange={(e) => setPromptContent(e.target.value)}
                        className="w-full px-3 py-2 rounded-md border"
                        style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)', minHeight: 220, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                        placeholder="Enter the full prompt text..."
                      />
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      <thead>
                        <tr style={{ color: 'var(--charcoal-light)' }}>
                          <th className="text-left py-2">Name</th>
                          <th className="text-left py-2">Agent</th>
                          <th className="text-left py-2">Industry</th>
                          <th className="text-left py-2">Proposal type</th>
                          <th className="text-left py-2">Updated</th>
                          <th className="text-right py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {systemPrompts.map((p) => (
                          <tr key={p.id} className="border-t" style={{ borderColor: 'var(--sand)' }}>
                            <td className="py-3 pr-4" style={{ color: 'var(--charcoal)' }}>
                              {p.name}
                            </td>
                            <td className="py-3 pr-4" style={{ color: 'var(--charcoal-light)' }}>
                              {p.agent_name}
                            </td>
                            <td className="py-3 pr-4" style={{ color: 'var(--charcoal-light)' }}>
                              {p.industry || ''}
                            </td>
                            <td className="py-3 pr-4" style={{ color: 'var(--charcoal-light)' }}>
                              {p.proposal_type || ''}
                            </td>
                            <td className="py-3 pr-4" style={{ color: 'var(--charcoal-light)' }}>
                              {new Date(p.updated_at).toLocaleString()}
                            </td>
                            <td className="py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {(isAdmin || (!isAdmin && promptView === 'mine')) && (
                                  <button
                                    onClick={() => startEditPrompt(p)}
                                    className="px-3 py-2 rounded-md"
                                    style={{ background: 'rgba(200, 88, 53, 0.08)', color: 'var(--terracotta)' }}
                                  >
                                    Edit
                                  </button>
                                )}
                                {(isAdmin || (!isAdmin && promptView === 'mine')) && (
                                  <button
                                    onClick={() => deletePrompt(p)}
                                    className="px-3 py-2 rounded-md"
                                    style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#991b1b' }}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 text-sm" style={{ color: 'var(--charcoal-light)', fontFamily: 'DM Sans, sans-serif' }}>
                    {isLoadingPrompts ? 'Loading…' : `${systemPrompts.length} prompts`}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
